import { createContext, FC, useContext, useState } from 'react';
import { Divider, Tabs } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import {
	getFRMVisitReportDetail,
	VisitReportDetail,
} from '@/libs/fleet-api/services/visit-reports';
import FRMVisitReportTechnicalAspect from './VisitReportOfficePage.TechnicalAspect';
import FRMVisitReportOfficeCollateral from './VisitReportOfficePage.Collateral';
import MainInformation from '@/components/MainInformation';
import FRMVisitReportMarketingAspect from './VisitReportOfficePage.MarketingAspect';
import FRMVisitReportOfficeWarehouse from './VisitReportOfficePage.Warehouse';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const useGetFRMVisitReportDetail = (prospectId: string) => {
	return useQuery({
		queryKey: ['getFRMVisitReportDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getFRMVisitReportDetail(id);
		},
	});
};

const visitReportPageContext = createContext<ReturnType<
	typeof useGetFRMVisitReportDetail
> | null>(null);

const { Provider } = visitReportPageContext;

export const useFRMVisitReportPageContext = () => {
	const { data } = useContext(visitReportPageContext)!;
	return data?.data ?? ({} as Partial<VisitReportDetail>);
};

type FRMVisitReportPageProps = {
	prospectId: string;
};

const FRMVisitReportPage: FC<FRMVisitReportPageProps> = ({
	prospectId = '943P2024090008',
}) => {
	const query = useGetFRMVisitReportDetail(prospectId);
	const { data, isLoading, isFetching, isError } = query;
	const {
		mainInformation,
		surveyInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
	} = data?.data ?? {};

	const [activeTab, setActiveTab] = useState<string>('teknis');

	if (isError) return <GenericErrorMessage />;
	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Provider value={query}>
			<MainInformation
				mainInformation={mainInformation}
				surveyInformation={surveyInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				customerReferralDocuments={customerReferralDocuments}
				isLoading={isLoading}
			/>
			<Divider />
			<FleetTitle>Observasi Kantor</FleetTitle>
			<Tabs
				activeKey={activeTab}
				onChange={(key) => setActiveTab(key)}
				items={[
					{
						key: 'teknis',
						label: 'Aspek Teknis',
						children: (
							<FRMVisitReportTechnicalAspect
								onSave={() => setActiveTab('pemasaran')}
							/>
						),
					},
					{
						key: 'pemasaran',
						label: 'Aspek Pemasaran',
						children: (
							<FRMVisitReportMarketingAspect
								onSave={() => setActiveTab('workshop')}
							/>
						),
					},
					{
						key: 'workshop',
						label: 'Workshop/Gudang',
						children: (
							<FRMVisitReportOfficeWarehouse
								onSave={() => {
									setActiveTab('agunan');
								}}
							/>
						),
					},
					{
						key: 'agunan',
						label: 'Agunan',
						children: <FRMVisitReportOfficeCollateral />,
					},
				]}
			/>
		</Provider>
	);
};

export default FRMVisitReportPage;
